import { useState } from 'react';
import { businessDisplayNumber } from '../../shared/lib/businessNumber.js';
import { formatPhoneDisplay } from '../../shared/lib/phoneFormat.js';
import { useNavigate } from 'react-router-dom';
import { COUNTRY_LABELS } from '@acs/shared';
import { Badge, Button, Card, EmptyState, Icon, PageHeader, Skeleton, StatusDot } from '@acs/ui';
import type { BadgeTone } from '@acs/ui';
import { useMyBusinesses } from '../../app/BusinessesContext.js';
import { StatBadges } from '../appointments/StatBadges.js';
import { needsOnboarding } from '../onboarding/index.js';
import type { BusinessDoc } from '../../shared/api/converters.js';
import { useI18n } from '../../i18n/index.js';
import type { TranslationKey } from '../../i18n/index.js';

/** Assistant languages by their own name — endonyms are never translated. */
const LANGUAGE_NAME: Record<string, string> = { fr: 'Français', de: 'Deutsch', en: 'English', pl: 'Polski' };

/** Tone only — the words live in the catalogue under `businesses.status.*`. */
const STATUS_TONE: Record<string, BadgeTone> = {
  draft: 'info',
  collecting_docs: 'info',
  pending_review: 'warning',
  rejected: 'danger',
  approved: 'success',
  provisioning: 'info',
  active: 'success',
  suspended: 'danger',
  archived: 'neutral',
};

/** Every business on the account (design template — Portal · Businesses). */
export function BusinessesListPage() {
  const { t } = useI18n();
  const { active, businesses, loading } = useMyBusinesses();
  const navigate = useNavigate();
  const [showArchived, setShowArchived] = useState(false);

  const archived = businesses.filter((b) => b.status === 'archived');

  if (loading) {
    return (
      <>
        <PageHeader title={t('businesses.title')} />
        <div className="biz-grid">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height={180} radius="var(--tc-radius-md)" />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('businesses.title')}
        subtitle={t('businesses.subtitle')}
        actions={
          <Button leadingIcon={<Icon name="plus" size={16} />} onClick={() => navigate('/businesses/new')}>
            {t('businesses.new')}
          </Button>
        }
      />

      {active.length === 0 ? (
        <EmptyState
          icon={<Icon name="building" size={24} />}
          title={t('businesses.empty.title')}
          description={t('businesses.empty.description')}
          actions={
            <Button leadingIcon={<Icon name="plus" size={16} />} onClick={() => navigate('/businesses/new')}>
              {t('businesses.addFirst')}
            </Button>
          }
        />
      ) : (
        <div className="biz-grid">
          {active.map((b) => (
            <BusinessCard key={b.id} business={b} />
          ))}
        </div>
      )}

      {archived.length > 0 && (
        <>
          <div style={{ marginTop: 24 }}>
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<Icon name={showArchived ? 'chevron-down' : 'chevron-right'} size={16} />}
              aria-expanded={showArchived}
              onClick={() => setShowArchived((v) => !v)}
            >
              {t('businesses.archivedToggle', { count: archived.length })}
            </Button>
          </div>
          {showArchived && (
            <div className="biz-grid" style={{ marginTop: 12 }}>
              {archived.map((b) => (
                <Card key={b.id} title={b.name} actions={<Badge tone="neutral">{t('businesses.archived')}</Badge>}>
                  <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>{COUNTRY_LABELS[b.country]}</p>
                  <Button variant="secondary" onClick={() => navigate(`/b/${b.id}`)}>
                    {t('businesses.openHistory')}
                  </Button>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
}

function BusinessCard({ business }: { business: BusinessDoc }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const tone = STATUS_TONE[business.status] ?? 'neutral';
  const setupPending = needsOnboarding(business);
  const inPipeline = business.status !== 'active' && business.status !== 'suspended';
  // A finished business still waiting on a number lands on the number panel;
  // anything live opens Settings. (An unfinished one has no secondary — see
  // the buttons below.)
  const secondary = inPipeline
      ? { to: 'settings?section=number', labelKey: 'businesses.yourNumber' as TranslationKey }
      : { to: 'settings', labelKey: 'businesses.settings' as TranslationKey };

  return (
    <Card
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <StatusDot tone={business.status === 'active' ? 'success' : tone === 'danger' ? 'danger' : 'warning'} />
          {business.name}
        </span>
      }
      actions={<Badge tone={tone}>{t(`businesses.status.${business.status}` as TranslationKey)}</Badge>}
    >
      {businessDisplayNumber(business).number ? (
        <p className="biz-card__number">
          {formatPhoneDisplay(businessDisplayNumber(business).number)}
        </p>
      ) : (
        <p className="muted" style={{ margin: '0 0 2px' }}>{t('businesses.noNumber')}</p>
      )}
      <p className="muted" style={{ fontSize: 13, margin: '0 0 14px' }}>
        {COUNTRY_LABELS[business.country]} · {LANGUAGE_NAME[business.language] ?? business.language}
      </p>

      {/* The day's numbers on the card itself — the answer to "is this one
          working?" without opening it, which is the question this page is for
          once an account has more than one business.

          Not shown while setup is outstanding: that card's whole message is
          "finish this", and a row of zeroes underneath it would read as a
          verdict on a business that has not started rather than as news. It also
          costs a live calls subscription per card, which is worth paying for a
          business that takes calls and not for one that cannot yet. */}
      {!setupPending && <StatBadges business={business} />}

      <div className="row" style={{ gap: 8 }}>
        {/* An unfinished business has exactly one door — the gate would send
            "Open" into the wizard anyway, so offering both buttons would be two
            labels for one destination, one of which lies about where it goes. */}
        {setupPending ? (
          <Button onClick={() => navigate(`/b/${business.id}/setup`)}>
            {t('businesses.finishSetup')}
          </Button>
        ) : (
          <>
            <Button onClick={() => navigate(`/b/${business.id}`)}>{t('businesses.open')}</Button>
            <Button variant="secondary" onClick={() => navigate(`/b/${business.id}/${secondary.to}`)}>
              {t(secondary.labelKey)}
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}
